import { type ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { TooltipProvider } from '@monksflow/monks-ui';
import { motion } from 'motion/react';
import { usePageEntrance } from '@/shared/ui/animations';
import {
  ListPageBody,
  ListPageLayout,
  PageHeader,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderIdentity,
  PageHeaderToolGroup,
} from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import { StepTracker, ActionFooter } from '@/shared/ui';
import { STEP_COLUMN_MAX_WIDTH } from '../steps/shared';
import { useTranslation } from 'react-i18next';

interface StepValidationProps {
  onValidChange: (isValid: boolean) => void;
}

interface StepperPageTemplateProps {
  /** Names the wizard as the trailing crumb, e.g. "New IQ analysis". */
  title: string;
  stepLabels: string[];
  steps: ((props: StepValidationProps) => ReactNode)[];
  onSubmit?: () => void;
  isSubmitting?: boolean;
}

export const StepperPageTemplate = ({
  title,
  stepLabels,
  steps,
  onSubmit,
  isSubmitting = false,
}: StepperPageTemplateProps) => {
  const { t } = useTranslation('analysis');
  const entrance = usePageEntrance();

  const [activeStep, setActiveStep] = useState(0);
  const [canProceed, setCanProceed] = useState(false);
  const navigate = useNavigate();

  const iq = t('analysisResult.analysisResult.iq');

  const handleValidChange = useCallback((isValid: boolean) => {
    setCanProceed(isValid);
  }, []);

  const isLastStep = activeStep === steps.length - 1;

  const handleNext = () => {
    if (isLastStep && onSubmit) {
      onSubmit();
      return;
    }
    setCanProceed(true);
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setCanProceed(true);
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  return (
    <TooltipProvider>
      <div data-testid="dashboard-layout-padded" className="page-layout">
        <ListPageLayout>
          <motion.div {...entrance.header}>
            <PageHeader>
              <PageHeaderIdentity>
                <PageHeaderToolGroup
                  toolId="iq"
                  tool={iq}
                  category="plan"
                  onBadgeClick={() => void navigate(ROUTES.IQ)}
                >
                  <PageHeaderBreadcrumb>
                    <PageHeaderBreadcrumbLink href={ROUTES.IQ}>{iq}</PageHeaderBreadcrumbLink>
                    <PageHeaderBreadcrumbPage>{title}</PageHeaderBreadcrumbPage>
                  </PageHeaderBreadcrumb>
                </PageHeaderToolGroup>
              </PageHeaderIdentity>
            </PageHeader>
          </motion.div>

          {/* The wrapper carries the column sizing so the body still fills what the
              header leaves and keeps its own scroll bounds. */}
          <motion.div className="flex min-h-0 flex-1 flex-col" {...entrance.body}>
            {/* The tracker and the footer bracket the card; only the step between them scrolls,
                so the wizard's controls stay reachable however long a step runs. */}
            <ListPageBody>
              <StepTracker activeStep={activeStep} steps={stepLabels} />

              <div className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto p-section">
                <div className="flex w-full flex-col items-center gap-2.5">
                  {steps[activeStep]({ onValidChange: handleValidChange })}
                </div>
              </div>

              {/* Inset to match the step column above it, so the two line up at any width. */}
              <div className="w-full px-section py-2 border-t border-border-light">
                <ActionFooter
                  activeStep={activeStep}
                  totalSteps={steps.length}
                  onBack={handleBack}
                  onCancel={() => void navigate(ROUTES.IQ)}
                  enableNext={canProceed}
                  isSubmitting={isSubmitting}
                  onNext={handleNext}
                  maxWidth={STEP_COLUMN_MAX_WIDTH}
                />
              </div>
            </ListPageBody>
          </motion.div>
        </ListPageLayout>
      </div>
    </TooltipProvider>
  );
};
